import { sortBy } from 'lodash';

import { FileUploadStatus, GalleryImage } from '@phoenix/core/frontend/shared/ui/images/images-gallery/images-gallery';
import {
  AttachmentType,
  BarAndChainCombinationFavorite,
  BatteryFavorite,
  BatteryRuntime as GraphQLBatteryRuntime,
  CuttingAttachmentFavorite,
  Maybe,
  Product as GraphQLProduct,
  ProductAttachment as GraphQLProductAttachment,
  ProductCategory,
  ProductFavoriteStatus,
  ProductServiceEntry,
  ProductStatus,
  ProductToCreateInput,
  ServiceReminder as GraphQLServiceReminder,
  UnregisteredProduct as GraphQLUnregisteredProduct,
  SparePartFavorite,
  ServiceKitFavorite,
  ChargerFavorite,
  ChargerDetails,
  BatteryDetails,
  ProductAccessoriesResponse,
} from '@phoenix/team-m/my-tool-shed/frontend/domain/generated/graphql';

import {
  BatteryRuntime,
  ProductAccessory,
  ProductAttachment,
  ProductBattery,
  ProductChainAndBarCombination,
  ProductCuttingAttachment,
  ProductDetails,
  ProductImages,
  ProductListItem,
  ProductServiceKit,
  ProductServiceProtocol,
  ProductSparePart,
  ServiceReminder,
  ProductAccessoriesType as AccessoriesType,
  ProductAccessories,
  ProductCharger,
} from './product.model';
import { getOptimizedImage } from '../images/images-util';

const mapServiceReminder = ({ nextServiceDate, interval, reminderDate, remindByEmail }: GraphQLServiceReminder): ServiceReminder => ({
  nextServiceDate: nextServiceDate ?? undefined,
  interval: interval ?? undefined,
  remindByEmail: remindByEmail ?? undefined,
  reminderDate: reminderDate ?? undefined,
});

const mapProductCategory = (productCategory?: Maybe<ProductCategory>) =>
  productCategory
    ? {
        id: productCategory.id || undefined,
        name: productCategory.name,
        categoryGroup: {
          name: productCategory?.categoryGroup?.name || '',
        },
      }
    : undefined;

const mapProductImages = (product: GraphQLProduct): ProductImages => ({
  main: product.images?.defaultImageUrl ?? undefined,
  system: product.images?.stihlImageUrl ?? undefined,
});

export const mapToProductListItem = (product: GraphQLProduct): ProductListItem => {
  const brand = product.brand ? { id: product.brand.id || undefined, name: product.brand.name } : undefined;
  const productDriveType = product.productDriveType ? { name: product.productDriveType.name } : undefined;
  const isIMow = product.productCategory?.name === 'LawnMower' && productDriveType?.name === 'Automatic';

  return {
    id: product.id,
    productCategory: mapProductCategory(product.productCategory),
    serialNumber: product.serialNumber ?? undefined,
    materialNumber: product.materialNumber ?? undefined,
    productDriveType,
    brand,
    customName: product.customName ?? '',
    productName: product.productName ?? '',
    purchaseDate: product.purchaseDate ?? undefined,
    registrationDate: product.registrationDate ?? undefined,
    warrantyEndDate: product.warrantyEndDate ?? undefined,
    serviceReminder: product.serviceReminder ? mapServiceReminder(product.serviceReminder) : undefined,
    images: mapProductImages(product),
    isIMow,
    isVerifiedIMow: product.status === ProductStatus.Verified && isIMow,
    warrantyExtensionDate: product.warrantyExtensionDate ?? '',
    isWarrantyExtensionPossible: !!product.isWarrantyExtensionPossible,
  };
};

const mapToServiceProtocols = (productServiceEntries: ProductServiceEntry[]): ProductServiceProtocol[] => {
  return productServiceEntries.map((entry) => ({
    id: entry.id,
    name: entry.serviceProtocol.careProduct.name,
    file: entry.serviceProtocolFileUri,
    creationDate: entry.timestamp,
  }));
};

export const mapToDocumentAttachment = (attachment: GraphQLProductAttachment): ProductAttachment => ({
  id: attachment.id,
  url: attachment.url,
  imageVariants: attachment.imageVariants ?? undefined,
  mimeType: attachment.mimeType,
  originalFilename: attachment.originalFilename,
  createdAt: attachment.createdAt,
});

export const mapToGalleryImages = (product: GraphQLProduct, images: ProductImages): GalleryImage[] => {
  const galleryImages =
    product.attachments
      ?.filter(({ type }) => type === AttachmentType.ProfileImage)
      .map((image) => ({
        id: image.id,
        url: getOptimizedImage(image.imageVariants)?.url ?? image.url,
        thumbnail: (image.imageVariants?.thumbnail ?? image).url,
        originalFilename: image.originalFilename,
        status: FileUploadStatus.Uploaded,
        isDeletable: true,
        isMainImage: !!image.defaultProfileImage,
        isSystem: false,
      })) ?? [];

  if (images?.system) {
    galleryImages.push({
      id: 'system-image-id',
      url: images.system,
      thumbnail: images.system,
      originalFilename: `stihl_image_${product.productName}`,
      status: FileUploadStatus.Uploaded,
      isDeletable: false,
      isMainImage: images.system === images.main,
      isSystem: true,
    });
  }
  return galleryImages;
};

export const mapToProductDetails = (product: GraphQLProduct): ProductDetails => {
  const { images, ...productListItem } = mapToProductListItem(product);

  return {
    ...productListItem,
    images,
    articleNumber: product.articleNumber ?? undefined,
    serviceProtocols: mapToServiceProtocols(product.serviceEntries ?? []),
    userManualUrl: product.productManualFile?.url,
    documents: product.attachments?.filter(({ type }) => type === AttachmentType.Document).map(mapToDocumentAttachment),
    galleryImages: mapToGalleryImages(product, images),
    isPetrol: product.productDriveType?.name === 'Petrol' || product.productDriveType?.name === 'Gas',
  };
};

export const mapFromUnregisteredProduct = (product: GraphQLUnregisteredProduct): ProductListItem => {
  const productDriveType = product.productDriveType || undefined;
  const isIMow = product.productCategory?.name === 'LawnMower' && productDriveType?.name === 'Automatic';

  return {
    id: product.serialNumber,
    serialNumber: product.serialNumber,
    productName: product.productName || '',
    customName: undefined,
    productCategory: mapProductCategory(product.productCategory),
    productDriveType,
    purchaseDate: product.purchaseDate || undefined,
    warrantyEndDate: product.warrantyEndDate || undefined,
    registrationDate: product.registrationDate || undefined,
    images: { main: product.imageSrc ?? undefined, system: product.imageSrc ?? undefined },
    serviceReminder: product.serviceReminder ? mapServiceReminder(product.serviceReminder) : undefined,
    isIMow,
    isVerifiedIMow: false,
    questions: product.questions || undefined,
  };
};

export const mapToProductToRegister = (product: ProductListItem): ProductToCreateInput => ({
  serialNumber: product.serialNumber,
  productCategoryId: product.productCategory?.id,
  productDriveTypeId: product.productDriveType?.id,
  productName: product.productName,
  purchaseDate: product.purchaseDate,
  registrationDate: product.registrationDate,
  warrantyEndDate: product.warrantyEndDate,
});

const mapProductAccessoryStatus = ({
  isObsolete = false,
  status,
  id,
}: {
  isObsolete?: boolean;
  status: ProductFavoriteStatus;
  id?: string;
}): Pick<ProductAccessory, 'isFavorite' | 'isObsolete' | 'favoriteId'> => ({
  isFavorite: status === ProductFavoriteStatus.Active,
  isObsolete,
  ...(id && { favoriteId: id }),
});

export const mapToProductChainAndBarCombinationAccessory = (
  combinations?: BarAndChainCombinationFavorite[]
): ProductChainAndBarCombination[] =>
  combinations?.map<ProductChainAndBarCombination>(({ accessory: { guideBar, sawChain, materialNumber }, ...productAccessory }) => ({
    id: `${guideBar?.materialNumber}_${sawChain?.materialNumber}`,
    chain: sawChain,
    bar: guideBar,
    materialNumber,
    ...mapProductAccessoryStatus(productAccessory),
  })) || [];

export const mapToProductCuttingAttachmentAccessory = (cuttingAttachments?: CuttingAttachmentFavorite[]): ProductCuttingAttachment[] =>
  cuttingAttachments?.map(
    ({
      accessory: { productName, materialNumber, productTitle, articleNumber, category, accessoryUrl, imageUrl },
      ...productAccessory
    }) => ({
      id: materialNumber,
      materialNumber,
      imageUrl,
      productTitle,
      productName,
      category,
      articleNumber,
      ...(accessoryUrl && { accessoryUrl }),
      ...mapProductAccessoryStatus(productAccessory),
    })
  ) || [];

const mapRuntimes = (runtimes: Array<GraphQLBatteryRuntime | null>): BatteryRuntime[] =>
  runtimes
    .filter((runtime) => !!(runtime?.runtime || runtime?.powerLevel))
    .map(({ powerLevel, runtime }: GraphQLBatteryRuntime) => ({
      ...(powerLevel && { powerLevel }),
      ...(runtime && { runtime }),
    }));

export const mapToProductBatteryAccessory = (batteries?: BatteryFavorite[]): ProductBattery[] =>
  batteries?.map(
    ({
      accessory: { productName, articleNumber, materialNumber, imageUrl, accessoryUrl, runtimes, compatibleChargers },
      ...productAccessory
    }) => ({
      id: materialNumber,
      materialNumber,
      productName: productName ?? '',
      runtimes: mapRuntimes(runtimes),
      ...(compatibleChargers && {
        compatibleChargers: sortBy(compatibleChargers.filter((c) => c !== null) as ChargerDetails[], (c) => c.timeToFullCharge),
      }),
      ...(articleNumber && { articleNumber }),
      ...(imageUrl && { imageUrl }),
      ...(accessoryUrl && { accessoryUrl }),
      ...mapProductAccessoryStatus(productAccessory),
    })
  ) || [];

export const mapToProductChargerAccessory = (batteries?: ChargerFavorite[]): ProductCharger[] =>
  batteries?.map(
    ({ accessory: { productName, articleNumber, materialNumber, imageUrl, accessoryUrl, compatibleBatteries }, ...productAccessory }) => ({
      id: materialNumber,
      materialNumber,
      productName: productName ?? '',
      ...(articleNumber && { articleNumber }),
      ...(imageUrl && { imageUrl }),
      ...(accessoryUrl && { accessoryUrl }),
      ...mapProductAccessoryStatus(productAccessory),
      ...(compatibleBatteries && { compatibleBatteries: compatibleBatteries.filter((b) => b !== null) as BatteryDetails[] }),
    })
  ) || [];

export const mapToProductSpareParts = (spareParts?: SparePartFavorite[]): ProductSparePart[] =>
  spareParts?.map(({ accessory: { sparePartId, sparePartNameTranslation, imageUrl, accessoryUrl }, ...productAccessory }) => ({
    id: sparePartId,
    name: sparePartNameTranslation ?? '',
    ...(imageUrl && { imageUrl }),
    ...(accessoryUrl && { accessoryUrl }),
    ...mapProductAccessoryStatus(productAccessory),
  })) || [];

export const mapToProductServiceKits = (serviceKits?: ServiceKitFavorite[]): ProductServiceKit[] =>
  serviceKits?.map(({ accessory: { materialNumber, text, imageUrl, accessoryUrl, components }, ...productAccessory }) => ({
    id: materialNumber,
    materialNumber,
    name: text,
    ...(imageUrl && { imageUrl }),
    ...(components && { components }),
    ...(accessoryUrl && { accessoryUrl }),
    ...mapProductAccessoryStatus(productAccessory),
  })) || [];

export const mapProductAccessories = (accessories?: ProductAccessoriesResponse): ProductAccessories => ({
  [AccessoriesType.ChainAndBarCombination]: mapToProductChainAndBarCombinationAccessory(accessories?.barAndChainCombinations?.items),
  [AccessoriesType.CuttingAttachment]: mapToProductCuttingAttachmentAccessory(accessories?.cuttingAttachments?.items),
  [AccessoriesType.Battery]: mapToProductBatteryAccessory(accessories?.batteries?.items),
  [AccessoriesType.Charger]: mapToProductChargerAccessory(accessories?.chargers?.items),
  [AccessoriesType.SparePart]: mapToProductSpareParts(accessories?.spareParts?.items),
  [AccessoriesType.ServiceKit]: mapToProductServiceKits(accessories?.serviceKits?.items),
});
